import { BASE_URL } from 'config';
import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import Swal from "sweetalert2";
import axios from "axios";

const getFileTypeIcon = (fileName) => {
  if (!fileName) return <InsertDriveFileIcon color="action" fontSize="large" />;
  const ext = fileName.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext)) return null;
  if (ext === "pdf") return <PictureAsPdfIcon color="error" fontSize="large" />;
  if (["doc", "docx", "txt", "rtf"].includes(ext))
    return <DescriptionIcon color="primary" fontSize="large" />;
  return <InsertDriveFileIcon color="action" fontSize="large" />;
};

const isImage = (fileName) => {
  if (!fileName) return false;
  const ext = fileName.split(".").pop().toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
};

const StoreDocumentsEdit = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({
    business_name: "",
    commercial_registration_number: "",
    cr_expiry_date: "",
    municipal_license_number: "",
    municipal_license_expiry: "",
    owner_phone: "",
    unified_number: "",
    tax_number: "",
    cr_file: "",
    license_file: "",
    national_address_file: "",
    tax_file: "",
  });
  const [filePreviews, setFilePreviews] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      fetchDocs();
      setErrors({});
      setTouched({});
    }
    // eslint-disable-next-line
  }, [open]);

  const fetchDocs = async () => {
    setLoading(true);
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(BASE_URL + "/manager/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const d = data.data?.store?.documents || {};
      setDocs(d);
      setForm({
        business_name: d.business_name || "",
        commercial_registration_number: d.commercial_registration_number || "",
        cr_expiry_date: d.cr_expiry_date ? d.cr_expiry_date.substring(0, 10) : "",
        municipal_license_number: d.municipal_license_number || "",
        municipal_license_expiry: d.municipal_license_expiry
          ? d.municipal_license_expiry.substring(0, 10)
          : "",
        owner_phone: d.owner_phone || "",
        unified_number: d.unified_number || "",
        tax_number: d.tax_number || "",
        cr_file: d.cr_file_url || "",
        license_file: d.license_file_url || "",
        national_address_file: d.national_address_file_url || "",
        tax_file: d.tax_file_url || "",
      });
      setFilePreviews({
        cr_file: d.cr_file_url || "",
        license_file: d.license_file_url || "",
        national_address_file: d.national_address_file_url || "",
        tax_file: d.tax_file_url || "",
      });
    } catch (error) {
      setDocs(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateForm({ ...form, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateForm(form));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, [field]: file }));
      setTouched((prev) => ({ ...prev, [field]: true }));
      setErrors(validateForm({ ...form, [field]: file }));
      if (isImage(file.name)) {
        setFilePreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
      } else {
        setFilePreviews((prev) => ({ ...prev, [field]: file }));
      }
    }
  };

  const validateForm = (fields = form) => {
    const newErrors = {};
    if (!fields.business_name.trim()) newErrors.business_name = "اسم النشاط التجاري مطلوب";
    if (!fields.commercial_registration_number.trim())
      newErrors.commercial_registration_number = "رقم السجل التجاري مطلوب";
    if (!fields.cr_expiry_date.trim())
      newErrors.cr_expiry_date = "تاريخ انتهاء السجل التجاري مطلوب";
    if (!fields.cr_file) newErrors.cr_file = "ملف أو صورة السجل التجاري مطلوبة";
    if (!fields.owner_phone.trim()) newErrors.owner_phone = "رقم جوال المالك مطلوب";
    if (!fields.unified_number.trim()) newErrors.unified_number = "الرقم الموحد مطلوب";
    if (!fields.national_address_file)
      newErrors.national_address_file = "ملف أو صورة العنوان الوطني مطلوبة";
    return newErrors;
  };

  const isFormValid = Object.keys(validateForm(form)).length === 0;

  // معاينة الملف (صورة أو ملف نصي)
  const renderFilePreview = (file, field) => {
    if (!file) return null;
    if (typeof file === "string" && isImage(file)) {
      return (
        <Box mt={1} mb={1}>
          <img src={file} alt={field} style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />
        </Box>
      );
    }
    if (typeof file === "string" && !isImage(file)) {
      return (
        <Box mt={1} mb={1} display="flex" alignItems="center" gap={1}>
          {getFileTypeIcon(file)}
          <Button
            size="small"
            variant="outlined"
            color="info"
            href={file}
            target="_blank"
            rel="noopener"
          >
            معاينة الملف
          </Button>
        </Box>
      );
    }
    if (file instanceof File) {
      if (isImage(file.name)) {
        return (
          <Box mt={1} mb={1}>
            <img
              src={URL.createObjectURL(file)}
              alt={field}
              style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }}
            />
          </Box>
        );
      } else {
        return (
          <Box mt={1} mb={1} display="flex" alignItems="center" gap={1}>
            {getFileTypeIcon(file.name)}
            <Button
              size="small"
              variant="outlined"
              color="info"
              onClick={() => {
                const url = URL.createObjectURL(file);
                window.open(url, "_blank");
              }}
            >
              معاينة الملف
            </Button>
          </Box>
        );
      }
    }
    return null;
  };

  // دالة الحفظ
  const handleSave = async () => {
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    setTouched({
      business_name: true,
      commercial_registration_number: true,
      cr_expiry_date: true,
      cr_file: true,
      municipal_license_number: true,
      municipal_license_expiry: true,
      owner_phone: true,
      unified_number: true,
      tax_number: true,
      license_file: true,
      national_address_file: true,
      tax_file: true,
    });
    if (Object.keys(validationErrors).length > 0) return;
    try {
      const formPayload = new FormData();
      formPayload.append("business_name", form.business_name);
      formPayload.append("commercial_registration_number", form.commercial_registration_number);
      formPayload.append("cr_expiry_date", form.cr_expiry_date);
      formPayload.append("municipal_license_number", form.municipal_license_number);
      formPayload.append("municipal_license_expiry", form.municipal_license_expiry);
      formPayload.append("owner_phone", form.owner_phone);
      formPayload.append("unified_number", form.unified_number);
      formPayload.append("tax_number", form.tax_number);
      if (form.cr_file instanceof File) formPayload.append("cr_file", form.cr_file);
      if (form.license_file instanceof File) formPayload.append("license_file", form.license_file);
      if (form.national_address_file instanceof File)
        formPayload.append("national_address_file", form.national_address_file);
      if (form.tax_file instanceof File) formPayload.append("tax_file", form.tax_file);
      const token = localStorage.getItem("user-token");
      Swal.fire({
        title: "جاري حفظ البيانات...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
      // طباعة البيانات المرسلة
      for (let pair of formPayload.entries()) {
        console.log(pair[0], pair[1]);
      }
      const response = await axios.post(
        BASE_URL + "/manager/store-documents/update",
        formPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ وثائق المتجر بنجاح.",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "99999";
          },
        });
        if (onClose) onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage =
        error.response?.data?.message || error.message || "حدث خطأ أثناء حفظ البيانات";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>تعديل وثائق المتجر</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" display="flex" flexDirection="column" gap={3} mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      اسم النشاط التجاري <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="business_name"
                  value={form.business_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.business_name && touched.business_name}
                  helperText={
                    touched.business_name && errors.business_name ? errors.business_name : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      رقم السجل التجاري <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="commercial_registration_number"
                  value={form.commercial_registration_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={
                    !!errors.commercial_registration_number &&
                    touched.commercial_registration_number
                  }
                  helperText={
                    touched.commercial_registration_number && errors.commercial_registration_number
                      ? errors.commercial_registration_number
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      تاريخ انتهاء السجل التجاري <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="cr_expiry_date"
                  type="date"
                  value={form.cr_expiry_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.cr_expiry_date && touched.cr_expiry_date}
                  helperText={
                    touched.cr_expiry_date && errors.cr_expiry_date ? errors.cr_expiry_date : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={<span>رقم رخصة البلدية</span>}
                  name="municipal_license_number"
                  value={form.municipal_license_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={<span>تاريخ انتهاء رخصة البلدية</span>}
                  name="municipal_license_expiry"
                  type="date"
                  value={form.municipal_license_expiry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      رقم جوال المالك <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="owner_phone"
                  value={form.owner_phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.owner_phone && touched.owner_phone}
                  helperText={touched.owner_phone && errors.owner_phone ? errors.owner_phone : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      الرقم الموحد <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="unified_number"
                  value={form.unified_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.unified_number && touched.unified_number}
                  helperText={
                    touched.unified_number && errors.unified_number ? errors.unified_number : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={<span>الرقم الضريبي</span>}
                  name="tax_number"
                  value={form.tax_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              {/* ملفات وصور */}
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <span style={{ fontWeight: "bold" }}>
                    ملف أو صورة السجل التجاري <span style={{ color: "red" }}>*</span>
                  </span>
                  <input
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    style={{ display: "none" }}
                    id="cr-file-upload"
                    type="file"
                    onChange={(e) => handleFileChange(e, "cr_file")}
                  />
                  <label htmlFor="cr-file-upload">
                    <IconButton color="primary" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {renderFilePreview(filePreviews.cr_file || form.cr_file, "cr_file")}
                  {touched.cr_file && errors.cr_file && (
                    <Box color="error.main" fontSize={13}>
                      {errors.cr_file}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <span style={{ fontWeight: "bold" }}>ملف أو صورة الرخصة البلدية</span>
                  <input
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    style={{ display: "none" }}
                    id="license-file-upload"
                    type="file"
                    onChange={(e) => handleFileChange(e, "license_file")}
                  />
                  <label htmlFor="license-file-upload">
                    <IconButton color="primary" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {renderFilePreview(
                    filePreviews.license_file || form.license_file,
                    "license_file"
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <span style={{ fontWeight: "bold" }}>
                    ملف أو صورة العنوان الوطني <span style={{ color: "red" }}>*</span>
                  </span>
                  <input
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    style={{ display: "none" }}
                    id="national-address-file-upload"
                    type="file"
                    onChange={(e) => handleFileChange(e, "national_address_file")}
                  />
                  <label htmlFor="national-address-file-upload">
                    <IconButton color="primary" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {renderFilePreview(
                    filePreviews.national_address_file || form.national_address_file,
                    "national_address_file"
                  )}
                  {touched.national_address_file && errors.national_address_file && (
                    <Box color="error.main" fontSize={13}>
                      {errors.national_address_file}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <span style={{ fontWeight: "bold" }}>ملف أو صورة الرقم الضريبي</span>
                  <input
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    style={{ display: "none" }}
                    id="tax-file-upload"
                    type="file"
                    onChange={(e) => handleFileChange(e, "tax_file")}
                  />
                  <label htmlFor="tax-file-upload">
                    <IconButton color="primary" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {renderFilePreview(filePreviews.tax_file || form.tax_file, "tax_file")}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="error"
          variant="outlined"
          sx={{ color: "white !important", backgroundColor: "red !important", border: "none" }}
        >
          إلغاء
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!isFormValid}
          sx={{ color: "white !important" }}
          onClick={handleSave}
        >
          حفظ التعديلات
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreDocumentsEdit;
